<template>
  <div class="agro-ia-chatbot-card" data-tour-id="agroia-root">
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <div class="tableContainerDetails" data-tour-id="agroia-panel">
      <div class="chatbot-actions" data-tour-id="agroia-actions">
        <!-- Entrada al chat: si no existe interpretación, se genera automáticamente antes de abrir -->
        <f7-button
          class="btn-secondary chatbot-trigger-btn"
          fill
          outline
          type="default"
          data-tour-id="agroia-chat-trigger"
          @click="openChatPopup"
        >
          <span class="chatbot-trigger-content">
            <span class="chatbot-trigger-icon"></span>
            <span>{{ $t('agroIA.askAboutSample') }}</span>
          </span>
        </f7-button>
        <!-- Mostrar solo acciones pendientes para la muestra activa -->
        <div
          v-if="
            showGenerateInterpretationButton ||
            showGenerateLimitsButton
          "
          class="summary-actions"
          data-tour-id="agroia-report-actions"
        >
          <f7-button
            v-if="showGenerateInterpretationButton"
            class="btn-secondary generate-interpretation-btn"
            fill
            outline
            type="default"
            data-tour-id="agroia-generate-interpretation"
            @click="generateInterpretationAndDiagnosis"
          >
            <span class="generate-interpretation-content">
              <span
                class="interpretation-ai-icon generate-interpretation-icon"
              ></span>
              <span>{{ $t('agroIA.generateInitialStudy') }}</span>
            </span>
          </f7-button>
          <f7-button
            v-if="showGenerateLimitsButton"
            class="btn-secondary generate-interpretation-btn"
            fill
            outline
            type="default"
            data-tour-id="agroia-generate-limits"
            @click="generateLimitsAndCorrections"
          >
            <span class="generate-interpretation-content">
              <span
                class="limits-corrections-icon generate-interpretation-icon"
              ></span>
              <span>{{ $t('agroIA.limitsAndCorrections') }}</span>
            </span>
          </f7-button>
          <!-- <f7-button
            class="btn-secondary"
            fill
            outline
            type="default"
            @click=""
          >
            Reportar incidencia
          </f7-button> -->
        </div>
      </div>
      <div data-tour-id="agroia-reports-section">
        <div
          v-if="currentSampleInterpretationAndDiagnosis !== ''"
          id="interpretation-section"
          class="chatbot-container"
          data-tour-id="agroia-interpretation-card"
        >
          <div class="chatbot-header">
            <div class="summary-title">
              <div class="interpretation-ai-icon"></div>
              <div class="header-label">
                {{ $t('agroIA.interpretationAndDiagnosis') }}
              </div>
            </div>
            <div class="chatbot-header-actions">
              <button
                v-if="showInterpretationAndDiagnosisText"
                class="reload-icon-button"
                type="button"
                :aria-label="$t('agroIA.repeatReportTooltip')"
                :title="$t('agroIA.repeatReportTooltip')"
                @click="repeatInterpretationAndDiagnosis"
              >
                <span class="reload-icon"></span>
              </button>
              <button
                class="report-toggle-icon-button"
                type="button"
                :aria-label="
                  showInterpretationAndDiagnosisText
                    ? $t('agroIA.viewLess')
                    : $t('agroIA.viewMore')
                "
                :title="
                  showInterpretationAndDiagnosisText
                    ? $t('agroIA.viewLess')
                    : $t('agroIA.viewMore')
                "
                @click="toggleInterpretationAndDiagnosisText()"
              >
                <span
                  :class="[
                    'report-toggle-arrow',
                    showInterpretationAndDiagnosisText
                      ? 'is-up'
                      : 'is-down',
                  ]"
                ></span>
              </button>
            </div>
          </div>

          <!-- Contenido y reintento visibles únicamente cuando la card está desplegada -->
          <div
            v-if="
              currentSampleInterpretationAndDiagnosis !== '' &&
              showInterpretationAndDiagnosisText
            "
            class="interpretation-content"
          >
            <div
              v-html="
                formatHTMLText(
                  currentSampleInterpretationAndDiagnosis
                )
              "
            ></div>
          </div>
        </div>

        <div
          v-if="currentSampleLimitsAndCorrections !== ''"
          id="limits-section"
          class="chatbot-container"
          data-tour-id="agroia-limits-card"
        >
          <div class="chatbot-header">
            <div class="summary-title">
              <div
                class="interpretation-ai-icon limits-corrections-icon"
              ></div>
              <div class="header-label">
                {{ $t('agroIA.limitsAndCorrections') }}
              </div>
            </div>
            <div class="chatbot-header-actions">
              <button
                v-if="showLimitsAndCorrectionsText"
                class="reload-icon-button"
                type="button"
                :aria-label="$t('agroIA.repeatReportTooltip')"
                :title="$t('agroIA.repeatReportTooltip')"
                @click="repeatLimitsAndCorrections"
              >
                <span class="reload-icon"></span>
              </button>
              <button
                class="report-toggle-icon-button"
                type="button"
                :aria-label="
                  showLimitsAndCorrectionsText
                    ? $t('agroIA.viewLess')
                    : $t('agroIA.viewMore')
                "
                :title="
                  showLimitsAndCorrectionsText
                    ? $t('agroIA.viewLess')
                    : $t('agroIA.viewMore')
                "
                @click="toggleLimitsAndCorrectionsText()"
              >
                <span
                  :class="[
                    'report-toggle-arrow',
                    showLimitsAndCorrectionsText
                      ? 'is-up'
                      : 'is-down',
                  ]"
                ></span>
              </button>
            </div>
          </div>
          <!-- Mismo patrón de despliegue/reintento para límites -->
          <div
            v-if="
              currentSampleLimitsAndCorrections !== '' &&
              showLimitsAndCorrectionsText
            "
            class="interpretation-content"
          >
            <div
              v-html="
                formatHTMLText(currentSampleLimitsAndCorrections)
              "
            ></div>
          </div>
        </div>
      </div>
    </div>
    <AgroIAChatPanel
      v-if="showChatPopup"
      :cod-muestra="currentCodMuestra"
      :interpretation="currentSampleInterpretationAndDiagnosis"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import marked from 'marked';
import DOMPurify from 'dompurify';
import {
  AGROIA_GUIDED_TOUR_AUTO_START_DELAY,
  IS_LOCAL_TEST,
  NOTIFY_TIME_LONG,
} from '@/js/constants';
import AgroIAChatPanel from '@/components/Agronomy/AgroIAChatbot/AgroIAChatPanel.vue';
import {
  shouldAutoStartAgroIAGuidedTour,
  startAgroIAGuidedTour,
  stopAgroIAGuidedTour,
} from '@/services/agroIAGuidedTour';

export default {
  name: 'AgroIAChatbot',
  components: {
    AgroIAChatPanel,
  },
  data() {
    return {
      currentSampleSummary: {},
      loaded: false,
      showInterpretationAndDiagnosisText: false,
      showLimitsAndCorrectionsText: false,
      guidedTourStartTimeoutId: null,
      hasTriedAutoStartGuidedTour: false,
    };
  },
  computed: {
    currentCodMuestra() {
      return (
        (this.currentSampleSummary &&
          this.currentSampleSummary.code) ||
        ''
      );
    },
    ...mapState('contact', ['enabled']),
    ...mapState('samples', ['sampleDetailReloadInProgress']),
    ...mapState('agroIA', [
      'showChatPopup',
      'sampleInterpretationAndDiagnosisBySample',
      'sampleLimitsAndCorrectionsBySample',
    ]),
    currentSampleInterpretationAndDiagnosis() {
      // Estado normalizado por muestra para evitar mezclar datos entre detalles
      return (
        this.sampleInterpretationAndDiagnosisBySample[
          this.currentCodMuestra
        ] || ''
      );
    },
    currentSampleLimitsAndCorrections() {
      // Estado normalizado por muestra para límites y correcciones
      return (
        this.sampleLimitsAndCorrectionsBySample[
          this.currentCodMuestra
        ] || ''
      );
    },
    hasInterpretationAndDiagnosis() {
      return !!(
        this.currentSampleInterpretationAndDiagnosis || ''
      ).trim();
    },
    hasLimitsAndCorrections() {
      return !!(this.currentSampleLimitsAndCorrections || '').trim();
    },
    showGenerateInterpretationButton() {
      return !this.hasInterpretationAndDiagnosis;
    },
    showGenerateLimitsButton() {
      return !this.hasLimitsAndCorrections;
    },
    shouldWaitForStableDetail() {
      return this.sampleDetailReloadInProgress;
    },
  },
  watch: {
    loaded(value) {
      if (value) {
        this.scheduleGuidedTourStart();
      }
    },
    currentCodMuestra() {
      this.scheduleGuidedTourStart();
    },
    sampleDetailReloadInProgress(value) {
      if (!value && this.loaded && this.currentCodMuestra) {
        this.scheduleGuidedTourStart({ force: false });
      }
    },
  },
  async beforeMount() {
    try {
      // Obtiene la muestra seleccionada en filtros para ligar toda la UI al mismo contexto
      this.currentSampleSummary =
        await this.getCurrentSampleBasedOnFilter();
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              JSON.parse(error.message).error
            )
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.loaded = true;
    }
  },
  mounted() {
    this.scheduleGuidedTourStart();
  },
  beforeDestroy() {
    this.clearGuidedTourStartTimeout();
    stopAgroIAGuidedTour(this);
  },
  methods: {
    getAgroIAAnalyticsContext() {
      const authenticatedUser =
        this.$store?.state?.user?.authenticatedUser || {};
      const contact = this.$store?.state?.contact || {};
      const contactInfo =
        this.$store?.getters?.['contact/contactInfo'] || {};
      const language =
        authenticatedUser?.language_code ||
        authenticatedUser?.contacts?.[0]?.language;
      const companyCode =
        contact?.company ||
        authenticatedUser?.contacts?.[0]?.companies?.[0]?.code;
      // Pais para agregacion en GA:
      // 1) si el contacto activo expone pais, usamos ese valor.
      // 2) si no, usamos el prefijo de la empresa (ES, CL, US).
      const countryCode =
        contactInfo?.country ||
        (typeof companyCode === 'string' &&
        companyCode.trim().length >= 2
          ? companyCode.trim().substring(0, 2)
          : undefined);

      return {
        sample_code: this.currentSampleSummary?.code || undefined,
        company_code: companyCode || undefined,
        country_code: countryCode || undefined,
        user_language: language || undefined,
      };
    },
    trackAgroIAEvent(eventName, extraParams = {}) {
      if (IS_LOCAL_TEST) return;

      window.gtag?.('event', eventName, {
        ...this.getAgroIAAnalyticsContext(),
        ...extraParams,
      });
    },
    clearGuidedTourStartTimeout() {
      if (this.guidedTourStartTimeoutId) {
        clearTimeout(this.guidedTourStartTimeoutId);
        this.guidedTourStartTimeoutId = null;
      }
    },
    scheduleGuidedTourStart({ force = false } = {}) {
      if (!this.loaded || !this.currentCodMuestra) return;
      if (this.shouldWaitForStableDetail) return;
      if (!force && this.hasTriedAutoStartGuidedTour) return;

      if (!force) {
        this.hasTriedAutoStartGuidedTour = true;
      }

      if (
        !shouldAutoStartAgroIAGuidedTour(this.$store, {
          force,
        })
      ) {
        return;
      }

      this.clearGuidedTourStartTimeout();
      this.guidedTourStartTimeoutId = setTimeout(() => {
        this.guidedTourStartTimeoutId = null;
        startAgroIAGuidedTour(this, { force });
      }, AGROIA_GUIDED_TOUR_AUTO_START_DELAY);
    },
    formatHTMLText(texto) {
      const normalized = (texto || '').replace(/\\n/g, '\n');

      // marked@4 -> parse
      const rawHtml = marked.parse(normalized, {
        breaks: true,
        gfm: true,
      });

      // Sanitiza para evitar XSS
      return DOMPurify.sanitize(rawHtml);
    },
    async generateInterpretationAndDiagnosis() {
      try {
        this.trackAgroIAEvent('agroia_initial_study_request');
        // Generación estándar, si existe en BBDD lo devuelve, si no lo genera de AgroIA y persiste
        this.loaded = false;
        await this.getSampleInterpretationAndDiagnosis({
          codMuestra: this.currentSampleSummary?.code,
        });
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.loaded = true;
        this.showInterpretationAndDiagnosisText = true;
      }
    },
    async generateLimitsAndCorrections() {
      try {
        this.trackAgroIAEvent('agroia_limits_request');
        // Generación estándar de límites para la muestra visible
        this.loaded = false;
        await this.getSampleLimitsAndCorrections({
          codMuestra: this.currentSampleSummary?.code,
        });
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.loaded = true;
        this.showLimitsAndCorrectionsText = true;
      }
    },
    async repeatInterpretationAndDiagnosis() {
      try {
        this.trackAgroIAEvent('agroia_initial_study_repeat');
        // Repetición explícita para estudio inicial, forceNew a True para reemplazar BBDD
        this.loaded = false;
        await this.getSampleInterpretationAndDiagnosis({
          codMuestra: this.currentSampleSummary?.code,
          forceNew: true,
        });
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.loaded = true;
        this.showInterpretationAndDiagnosisText = true;
      }
    },
    async repeatLimitsAndCorrections() {
      try {
        this.trackAgroIAEvent('agroia_limits_repeat');
        // Repetición explícita para límites y correcciones
        this.loaded = false;
        await this.getSampleLimitsAndCorrections({
          codMuestra: this.currentSampleSummary?.code,
          forceNew: true,
        });
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.loaded = true;
        this.showLimitsAndCorrectionsText = true;
      }
    },
    toggleInterpretationAndDiagnosisText() {
      this.showInterpretationAndDiagnosisText =
        !this.showInterpretationAndDiagnosisText;
    },
    toggleLimitsAndCorrectionsText() {
      this.showLimitsAndCorrectionsText =
        !this.showLimitsAndCorrectionsText;
    },
    async openChatPopup() {
      // El chat necesita un contexto inicial; si falta interpretacion se genera primero
      if (!this.hasInterpretationAndDiagnosis) {
        await this.generateInterpretationAndDiagnosis();
      }
      this.trackAgroIAEvent('agroia_chat_open');
      this.setShowChatPopup(true);
    },
    ...mapActions('samples', [
      'setReportSampleId',
      'getCurrentSampleBasedOnFilter',
      'getReport',
      'setSampleFetchedFromEtl',
    ]),
    ...mapActions('agroIA', [
      'getSampleInterpretationAndDiagnosis',
      'getSampleLimitsAndCorrections',
      'setShowChatPopup',
    ]),
    ...mapActions('breadcrumb', ['pushRoute', 'popRoute']),
  },
};
</script>

<style scoped>
.agro-ia-chatbot-card {
  padding: 30px 0 14px;
}
.chatbot-container {
  margin: 15px 0 5px;
  padding: 15px 20px;
  border-radius: 10px;
  border: 1px solid #8db6e2;
  background: var(--Basic-White, #fff);
}
.chatbot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.interpretation-ai-icon {
  width: 24px;
  height: 24px;
  background-image: url('@/static/icons/ic_interpretation_ai.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.header-label {
  color: var(--Primary-Blue-60, #04509f);
  font-weight: 600;
  line-height: 140%;
  font-size: 16px;
}
.link-button {
  color: var(--Neutral-70, #3e3c44);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
.btn-secondary {
  background-color: white !important;
  color: #00346a !important;
  border: 1px solid #00346a !important;
  font-weight: 600;
  border-radius: 10px;
  width: auto;
  padding: 1px 16px;
}
.btn-secondary:hover {
  background: #f5f9ff !important;
  border: 1px solid var(--Primary-Blue-70, #00346a) !important;
  box-shadow: 0 1px 4px 0 rgba(87, 84, 95, 0.3),
    0 1px 4px 0 rgba(87, 84, 95, 0.15);
}
.chatbot-trigger-btn {
  padding: 1px 14px 1px 10px !important;
}
.chatbot-trigger-content {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.chatbot-trigger-icon {
  width: 20px;
  height: 20px;
  background-image: url('@/static/icons/ic_chatbot_ai.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.summary-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.generate-interpretation-btn {
  padding: 1px 14px 1px 10px !important;
}
.generate-interpretation-content {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.generate-interpretation-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
}
.limits-corrections-icon {
  background-image: url('@/static/icons/ic_limits_corrections_ai.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.chatbot-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.reload-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.reload-icon {
  width: 21px;
  height: 21px;
  background-image: url('@/static/icons/ic_reload_blue.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.reload-icon-button:hover {
  opacity: 0.75;
}
.report-toggle-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.report-toggle-arrow {
  width: 8px;
  height: 8px;
  display: inline-block;
  position: relative;
  top: 0;
  border-right: 2.5px solid var(--Primary-Blue-70, #00346a);
  border-bottom: 2.5px solid var(--Primary-Blue-70, #00346a);
}
.report-toggle-arrow.is-down {
  transform: rotate(45deg);
}
.report-toggle-arrow.is-up {
  transform: rotate(-135deg);
}
.report-toggle-icon-button:hover {
  opacity: 0.75;
}
.interpretation-content {
  padding: 10px 16px 5px;
}
.chatbot-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
}
/* Label (ej: "Código de oferta") */
.label-strong {
  color: var(--Primary-Blue-70, #00346a);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 19.6px */
}

/* Valor (ej: "QCA-ES250900001") */
.value-text {
  color: var(--Neutral-70, #3e3c44);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
}

.tableContainerDetails {
  padding: 16px !important;
  border-radius: 10px !important;
  background: #ebf2fa !important;
  border: 1px solid var(--Primary-Blue-70, #00346a) !important;
}
.status-text {
  background: transparent !important;
  border: none !important;
  margin: auto;
  max-width: 100%;
}
</style>
